/**
 * Cortex Bridge Service
 * 
 * The central nervous system connecting Client-Web (The Body) to Cortex-API (The Brain).
 * 
 * Responsibilities:
 * 1. Maintain persistent SSE connection to Cortex API
 * 2. Route incoming SSE events to EventBus
 * 3. Standardize outgoing User Intent
 * 4. Manage optimistic UI updates
 */

import { bus } from '../../core/events.js';
import { streamCortex, type CortexRequest, type StreamCallbacks } from '../cortex-api/client.js';
import { log } from '../logging/index.js';

// Define bridge-specific events
export interface CortexBridgeEvents {
  'cortex.connected': { timestamp: number };
  'cortex.disconnected': { timestamp: number; reason?: string };
  'cortex.error': { error: string; timestamp: number };
  'cortex.intent.sent': { intent: string; payload: any; timestamp: number };
  'cortex.state.sync': { entity: string; id: string; action: 'create' | 'update' | 'delete'; data: any };
}

class CortexBridgeService {
  private isConnected: boolean = false;
  private currentStream: AbortController | null = null;
  private reconnectTimer: number | null = null;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 5;
  
  constructor() {
    // Auto-connect on initialization
    this.connect();
    
    // Listen for intent events from the UI
    bus.on('user.intent', (payload: unknown) => {
      const data = payload as { intent: string; data: any };
      this.sendIntent(data.intent, data.data);
    });

    // Listen for auth events to manage connection lifecycle
    bus.on('auth.login.success', () => {
      log.info('[CortexBridge] User logged in - reconnecting');
      this.reconnect();
    });

    bus.on('auth.logout.success', () => {
      log.info('[CortexBridge] User logged out - reconnecting anonymously');
      this.reconnect();
    });
  }

  /**
   * Force reconnection (e.g. on auth change)
   */
  public reconnect(): void {
    this.disconnect();
    this.reconnectAttempts = 0; // Reset attempts for intentional reconnect
    this.connect();
  }

  /**
   * Disconnect from Cortex API
   */
  public disconnect(): void {
    if (this.currentStream) {
      this.currentStream.abort();
      this.currentStream = null;
    }
    if (this.reconnectTimer) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.isConnected = false;
  }

  /**
   * Connect to the Cortex API SSE stream
   */
  public connect(): void {
    if (this.isConnected) return;

    log.info('[CortexBridge] Connecting to Cortex API...');

    const request: CortexRequest = {
      userInput: 'INIT_BRIDGE', // Special handshake message
      context: {
        client: 'web',
        version: '1.0.0',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      }
    };

    const callbacks: StreamCallbacks = {
      onConnected: (event) => {
        this.isConnected = true;
        this.reconnectAttempts = 0;
        bus.emit('cortex.connected', { timestamp: event.timestamp });
        log.info('[CortexBridge] Connected');
      },
      
      // Route specific events to the bus
      onCortexCompleted: (event) => {
        // 1. Emit generic response event
        bus.emit('cortex.response', event);
        
        // 2. Handle progressive visualizations (Streamed)
        // If a specific cortex (like 'empirical' or 'predictive') finishes with viz hints,
        // render them immediately without waiting for the final decision.
        if (event.visualizationHints && Array.isArray(event.visualizationHints)) {
          event.visualizationHints.forEach((hint: any) => {
            if (!hint.id) hint.id = `viz-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            bus.emit('visualization.signal', hint);
          });
        }
      },
      
      onError: (event) => {
        log.error('[CortexBridge] Error:', { error: event.error });
        bus.emit('cortex.error', { error: event.error, timestamp: event.timestamp });
        this.handleDisconnect();
      },
      
      onComplete: () => {
        log.info('[CortexBridge] Stream complete (unexpected for persistent bridge)');
        this.handleDisconnect();
      }
    };

    // In a real implementation, we'd want a persistent connection mode
    // For now, we use the existing streamCortex which is request-response oriented
    // We need to adapt this for long-lived connections or polling
    
    streamCortex(request, callbacks).then(controller => {
      this.currentStream = controller;
    }).catch(err => {
      log.error('[CortexBridge] Failed to start stream', err);
      this.handleDisconnect();
    });
  }

  /**
   * Send a user intent to the Cortex API
   */
  public async sendIntent(intent: string, data: any): Promise<void> {
    log.info(`[CortexBridge] Sending intent: ${intent}`, data);
    
    // Emit local event for optimistic UI
    bus.emit('cortex.intent.sent', { 
      intent, 
      payload: data, 
      timestamp: Date.now() 
    });

    // In the request-response model:
    const request: CortexRequest = {
      userInput: `INTENT:${intent} ${JSON.stringify(data)}`, // Simple protocol for now
      context: {
        intent,
        data
      }
    };

    // Start a new stream for this intent
    // Note: In a full bidirectional system, we'd send this over an existing channel
    // or HTTP POST while listening on the SSE
    await streamCortex(request, {
      onConversational: (event) => {
        // AI talking back
        bus.emit('chat.message.incoming', { 
          message: event.message,
          timestamp: event.timestamp
        });
      },
      onDecisionComplete: (event) => {
        // AI made a decision (e.g. created a todo)
        // Map decision to internal state event
        this.handleDecision(event.decision);
      },
      onError: (event) => {
        bus.emit('cortex.error', event);
      }
    });
  }

  private handleDecision(decision: any): void {
    // Map backend decisions to frontend state updates
    if (decision.type === 'create_todo') {
      bus.emit('todo.added.remote', decision.data);
    }
    
    // Handle visualizations
    if (decision.visualizationHints && Array.isArray(decision.visualizationHints)) {
      decision.visualizationHints.forEach((hint: any) => {
        // Ensure ID exists
        if (!hint.id) hint.id = `viz-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        bus.emit('visualization.signal', hint);
      });
    }
  }

  private handleDisconnect(): void {
    this.isConnected = false;
    bus.emit('cortex.disconnected', { timestamp: Date.now() });
    
    // Simple exponential backoff
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
      log.info(`[CortexBridge] Reconnecting in ${delay}ms...`);
      this.reconnectTimer = window.setTimeout(() => {
        this.reconnectAttempts++;
        this.connect();
      }, delay);
    }
  }
}

// Export singleton
export const cortexBridge = new CortexBridgeService();

